import { useQuery } from 'react-query';
import HttpService from 'services/httpService';
import { ENDPOINT_BASE_URL } from './constants';
import { useAuth } from './useAuth';
import { ExamTypes, FullJoints, ViewTypeData, ViewTypes } from 'models/XrayModels';

export type ReqViewsByAppointment = Record<ExamTypes, { [VT in ViewTypes]?: ViewTypeData<VT> }>

// see this section of the notion documentation for more information on how react query is utilized in this project:
// https://www.notion.so/JointAi-Comprehensive-Documentation-e8b94dcf09064573af647df6557b7bd1?pvs=4#074ef550cfc14c11b1b5488f0d8c5fff

export const appointmentQueryKeys = {
  appointmentExams: (appointmentId: string | undefined | null, dateISOString: string | undefined | null) => ['appointmentExams', { appointmentId, activeDate: dateISOString }],
};

export interface RequiredView {
  key: ViewTypes;
  label: string;
  value: string | string[];
  isOptional?: boolean;
}
export type RequireView = Record<ViewTypes, RequiredView>

// Maps an exam type to its required views
export type ExamViews = {
  missing_xrays: { exam_type: ExamTypes; view_type: ViewTypes, label: string }[];
  missing_pros: { joint: FullJoints }[];
  required_views: RequireView;
}

export type AppointmentExams = Record<ExamTypes, ExamViews>;

// Main response type for get_appointment_exams
export interface GetAppointmentExamsResponse {
  exams: Record<ExamTypes, ExamViews>;
}

interface UseAppointmentExamsDataOptions {
  selectedDateISOString?: string | null;
  appointmentId?: string | null;
}

export const useAppointmentExamsData = (opts?: UseAppointmentExamsDataOptions) => {
  const { user, accessToken } = useAuth();

  const date = opts?.selectedDateISOString;
  const appointmentId = opts?.appointmentId;

  const getAppointmentExams = async ({ queryKey }: any) => {
    const [, { appointmentId, activeDate }] = queryKey;

    const data = await HttpService.get<GetAppointmentExamsResponse>({
      url: `${ENDPOINT_BASE_URL}/appointments/${appointmentId}/exams?org_id=${user?.orgs[0].org_id}&appointment_date=${activeDate}`,
      token: accessToken,
    });

    return data;
  };

  const {
    data: appointmentExamsData,
    error: appointmentExamsError,
    isLoading: appointmentExamsLoading,
    refetch: refetchAppointmentExams,

  } = useQuery(
    appointmentQueryKeys.appointmentExams(appointmentId, date),
    getAppointmentExams);

  const appointmentExams = appointmentExamsData?.data?.exams;

  return {
    appointmentExams,
    appointmentExamsError,
    appointmentExamsLoading,
    refetchAppointmentExams,
  };
};
